/**
 * NumbersPage.tsx — the Number tab (Story N1/N2; wireframes states 1-6:
 * docs/superpowers/specs/2026-08-06-backoffice-numbers-panel-wireframes.md).
 *
 * Single-tenant v1 (BFF is configured for exactly one tenant, per spec) — one
 * row, not a list, mirroring `ConfigPage.tsx`'s single-tenant posture. The
 * Products tab (Stories N3-N5) is a separate story (E0-BO-NUM6) and is not
 * built here; the "Numbers" nav item in v1 goes straight to this panel.
 *
 * Four panel-level states, distinguished by three independent signals (never
 * layout alone): configured (green pill, no reason), not-configured (NO pill
 * at all — a neutral, non-error empty state, distinct from a grey pill),
 * degraded (grey `unknown` pill + a reason line, configured value still
 * renders — N1 AC3, one dead source never blanks the row), and unauthorized
 * (the shared `UnauthorizedScreen`, short-circuiting before any panel chrome
 * mounts). The audit-trail disclosure the wireframe shows (N2 AC6) is
 * EXPLICITLY OUT OF SCOPE for this story — capital-service exposes no read
 * endpoint for it yet (E0-BO-NUM10, not merged) — so it is omitted entirely,
 * never stubbed.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient } from '../../api/client';
import type { TenantNumberConfig } from '../../api/types';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { Button } from '../../components/Button';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDateTime } from '../leads/formatDateTime';
import { ClearNumberConfirm } from './ClearNumberConfirm';
import { NumberEditForm } from './NumberEditForm';
import { numberStatusTone } from './numberStatusTone';
import { useNumberConfig } from './useNumberConfig';
import floating from '../../lib/floating';

export interface NumbersPageProps {
  apiClient: ApiClient;
}

type Mode = 'view' | 'edit' | 'clearConfirm';
const NotConfiguredPanel = ({ onSetNumber }: { onSetNumber: () => void }) => {
  const { t } = useTranslation();
  return (
    <div
      data-testid="numbers-not-configured"
      className="flex flex-col items-start gap-3 rounded-xl bg-surface-2 p-5 ring-1 ring-inset ring-ink-3/10"
    >
      <p className="text-body font-medium text-ink-1">{t('numbers.notConfigured.heading')}</p>
      <p className="text-body text-ink-2">{t('numbers.notConfigured.body')}</p>
      <button
        type="button"
        data-testid="numbers-set-number"
        onClick={onSetNumber}
        className="inline-flex min-h-11 items-center rounded-lg bg-gold px-4 py-1.5 text-body font-medium text-on-gold hover:bg-gold/90"
      >
        {t('numbers.notConfigured.cta')}
      </button>
    </div>
  );
};
const DetailItem = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-0.5">
    <dt className="text-meta font-medium uppercase tracking-wide text-ink-3">{label}</dt>
    <dd className="text-body text-ink-1">{value}</dd>
  </div>
);

const ConfiguredPanel = ({
  config,
  locale,
  onChange,
  onClear,
}: {
  config: TenantNumberConfig;
  locale: string;
  onChange: () => void;
  onClear: () => void;
}) => {
  const { t } = useTranslation();
  const dash = '—';
  const tone = numberStatusTone(config.status);
  return (
    <div
      data-testid="numbers-configured"
      className="flex flex-col gap-4 rounded-xl bg-surface-1 p-5 ring-1 ring-inset ring-ink-3/10"
    >
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DetailItem
          label={t('numbers.fields.phoneNumberId')}
          value={config.phone_number_id ?? dash}
        />
        <DetailItem
          label={t('numbers.fields.displayNumber')}
          value={config.whatsapp_number_display ?? dash}
        />
        <DetailItem
          label={t('numbers.fields.updatedAt')}
          value={formatDateTime(config.updated_at, locale)}
        />
        <DetailItem label={t('numbers.fields.updatedBy')} value={config.updated_by ?? dash} />
      </dl>

      <div className="flex flex-col gap-1">
        <StatusBadge status={tone} />
        {config.status.reason != null ? (
          <p data-testid="numbers-status-reason" className="text-meta text-ink-3">
            {t('numbers.status.reasonPrefix', { reason: config.status.reason })}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-outline pt-4">
        <Button className="min-h-11" data-testid="numbers-change" onClick={onChange}>
          {t('numbers.actions.change')}
        </Button>
        <Button
          variant="danger"
          className="ml-auto min-h-11"
          data-testid="numbers-clear"
          onClick={onClear}
        >
          {t('numbers.actions.clear')}
        </Button>
      </div>
    </div>
  );
};

export const NumbersPage = ({ apiClient }: NumbersPageProps) => {
  const { t, i18n } = useTranslation();
  const { config, phase, refetch } = useNumberConfig(apiClient);
  const [mode, setMode] = useState<Mode>('view');

  if (phase === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  if (phase === 'loading' && config == null) {
    return (
      <section className="flex flex-col gap-4">
        <h1 className="text-h1 text-ink-1">{t('numbers.title')}</h1>
        <p role="status" data-testid="numbers-loading" className="text-body text-ink-2">
          {t('numbers.loading')}
        </p>
      </section>
    );
  }

  if (phase === 'error' && config == null) {
    return (
      <section className="flex flex-col gap-4">
        <h1 className="text-h1 text-ink-1">{t('numbers.title')}</h1>
        <div className="flex flex-col items-start gap-3" data-testid="numbers-error">
          <p role="alert" className="text-body text-status-down">
            {t('numbers.errorBody')}
          </p>
          <button
            type="button"
            data-testid="numbers-retry"
            onClick={() => floating(refetch())}
            className="inline-flex items-center rounded-lg bg-surface-2 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-1"
          >
            {t('numbers.retry')}
          </button>
        </div>
      </section>
    );
  }

  if (config == null) {
    // Defensive: 'ready' phase always has a config; satisfies the type guard.
    return null;
  }

  const afterWrite = async () => {
    setMode('view');
    await refetch();
  };

  const renderBranch1 = () => {
    if (mode === 'edit')
      return (
        <NumberEditForm
          apiClient={apiClient}
          onSaved={afterWrite}
          onCancel={() => setMode('view')}
        />
      );
    if (mode === 'clearConfirm' && config.phone_number_id != null)
      return (
        <ClearNumberConfirm
          apiClient={apiClient}
          phoneNumberId={config.phone_number_id}
          tenantName={config.tenant_name}
          onCleared={afterWrite}
          onCancel={() => setMode('view')}
        />
      );
    if (config.phone_number_id == null)
      return <NotConfiguredPanel onSetNumber={() => setMode('edit')} />;
    return (
      <ConfiguredPanel
        config={config}
        locale={i18n.language}
        onChange={() => setMode('edit')}
        onClear={() => setMode('clearConfirm')}
      />
    );
  };

  return (
    <section aria-labelledby="numbers-heading" className="flex flex-col gap-5">
      <h1 id="numbers-heading" className="text-h1 text-ink-1">
        {t('numbers.title')}
      </h1>

      <dl className="flex flex-col gap-1 text-body text-ink-2">
        <div className="flex gap-2">
          <dt className="font-medium text-ink-3">{t('numbers.tenant')}</dt>
          <dd>{config.tenant_name}</dd>
        </div>
      </dl>

      {renderBranch1()}
    </section>
  );
};
